// See README.md § "Order support selection" provenance. These are not labels — each is an audience
// predicate the refund workflow matches EXACTLY, so a value that drifts by one character routes the
// claim nowhere. Copied from iOS IntercomSelectionValue (OrderSupport/OrderSupportCoordinator.swift,
// PR #8719 / CO-1388); keys mirror the iOS case names. Don't tidy the punctuation.
export const ReasonValue = {
  orderNeverArrived: 'Order never arrived',
  itemsMissingOrIncorrect: 'Items missing or incorrect',
  orderArrivedLate: 'Order arrived late',
  orderArrivedDamagedOrPoor: 'Order arrived damaged or in poor condition',
  receivedWrongOrder: 'Received the wrong order',
  noDeliveryConfirmation: 'No delivery confirmation',
  // The apostrophe is U+2019, NOT an ASCII `'`. Near-indistinguishable on screen and only one
  // matches the audience, so "normalising" it would silently stop this reason routing.
  // reasons.test.ts asserts the codepoint via an escape, which is what makes a swap fail CI.
  deliveryDetailsMismatch: 'Delivery photo or address doesn’t match',
  markedDeliveredStillMoving: 'Driver marked it delivered, but is still moving',
  damagedOrSpilled: 'Damaged or spilled',
  coldOrSoggy: 'Cold or soggy',
  // iOS's sixth D1 option: the catch-all, routed to manual review with method 'Other'.
  somethingElse: 'Something else'
} as const

export type ReasonValueType = (typeof ReasonValue)[keyof typeof ReasonValue]

// Which terminal exception a guest hit. Keys mirror iOS's `OrderSupportException` cases; the ids
// are the analytics values both platforms report. Deliberately NOT a ReasonValue: an exception does
// not change what the guest said happened, so 'Order support selection' keeps the D1 reason and the
// exception rides on 'Selected refund method' — see EXCEPTION_REFUND_METHOD in dataMapper.ts.
export type ExceptionKind = 'alreadyReported' | 'outsideClaimWindow'

// refundable → D3 recovery choice; itemSelector → D2 items first; subMenu → sub-reasons;
// manualReview → quoted like refundable but D3 is skipped, the method being fixed (Reason.method).
// Mirrors iOS's routeQuotedDeliveryIssue, which quotes either way so the agent sees an estimate.
export type ReasonRoute =
  | 'refundable'
  | 'itemSelector'
  | 'subMenu'
  | 'manualReview'

// Mirrors dataMapper.ts's RefundMethod.ManualReview / .Other. Hand-written rather than imported:
// dataMapper.ts already imports ExceptionKind from here. dataMapper.test.ts keeps the two in sync.
export type FixedRefundMethod = 'Manual review' | 'Other'

export interface Reason {
  id: string
  i18nKey: string // `${i18nKey}.title` / `.description`
  // Every node carries one today, subMenu parents included — but a parent's value is never
  // submitted: only the leaf reason reaches 'Order support selection'.
  value?: ReasonValueType
  route: ReasonRoute
  // The fixed method a 'manualReview' leaf hands off with, plus 'items-missing-or-incorrect',
  // whose D2 flow also lands on manual review. Absent for a 'refundable' leaf: the guest picks.
  method?: FixedRefundMethod
  children?: Reason[]
}

export const RECOVERY_REASONS: Reason[] = [
  {
    id: 'order-never-arrived',
    i18nKey: 'order-confirmation.recovery.reason.order-never-arrived',
    value: ReasonValue.orderNeverArrived,
    route: 'subMenu',
    children: [
      {
        id: 'no-delivery-confirmation',
        i18nKey: 'order-confirmation.recovery.reason.no-delivery-confirmation',
        value: ReasonValue.noDeliveryConfirmation,
        route: 'refundable'
      },
      {
        id: 'delivery-details-mismatch',
        i18nKey: 'order-confirmation.recovery.reason.delivery-details-mismatch',
        value: ReasonValue.deliveryDetailsMismatch,
        // iOS routes this to manual review: a proof-photo or address conflict is not a claim
        // the server can safely auto-quote through to the guest, so an agent decides.
        route: 'manualReview',
        method: 'Manual review'
      },
      {
        id: 'marked-delivered-still-moving',
        i18nKey:
          'order-confirmation.recovery.reason.marked-delivered-still-moving',
        value: ReasonValue.markedDeliveredStillMoving,
        route: 'manualReview',
        method: 'Manual review'
      }
    ]
  },
  {
    id: 'items-missing-or-incorrect',
    i18nKey: 'order-confirmation.recovery.reason.items-missing-or-incorrect',
    value: ReasonValue.itemsMissingOrIncorrect,
    // D2 still runs, but per iOS it lands on manual review rather than D3's method choice.
    route: 'itemSelector',
    method: 'Manual review'
  },
  {
    id: 'order-arrived-late',
    i18nKey: 'order-confirmation.recovery.reason.order-arrived-late',
    value: ReasonValue.orderArrivedLate,
    route: 'refundable'
  },
  {
    id: 'order-arrived-damaged',
    i18nKey: 'order-confirmation.recovery.reason.order-arrived-damaged',
    value: ReasonValue.orderArrivedDamagedOrPoor,
    route: 'subMenu',
    children: [
      {
        id: 'damaged-or-spilled',
        i18nKey: 'order-confirmation.recovery.reason.damaged-or-spilled',
        value: ReasonValue.damagedOrSpilled,
        route: 'manualReview',
        method: 'Manual review'
      },
      {
        id: 'cold-or-soggy',
        i18nKey: 'order-confirmation.recovery.reason.cold-or-soggy',
        value: ReasonValue.coldOrSoggy,
        route: 'refundable'
      }
    ]
  },
  {
    id: 'received-wrong-order',
    i18nKey: 'order-confirmation.recovery.reason.received-wrong-order',
    value: ReasonValue.receivedWrongOrder,
    route: 'refundable'
  },
  {
    id: 'something-else',
    i18nKey: 'order-confirmation.recovery.reason.something-else',
    value: ReasonValue.somethingElse,
    // The catch-all: method 'Other', distinct from the other leaves' 'Manual review'.
    route: 'manualReview',
    method: 'Other'
  }
]
